import { initializeConnector } from '@web3-react/core'
import { MetaMask } from '@web3-react/metamask'
import { ethers } from 'ethers'
import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { SUPPORTED_CHAIN_IDS } from './connectors'
import { UnsupportedChainIdError } from './errors'

export const [metaMask, metaMaskHooks] = initializeConnector<MetaMask>(
  (actions) => new MetaMask({ actions, onError: console.error })
)

const ErrorContext = createContext<{
  error: Error | undefined
  setError: (error: Error | undefined) => void
}>({ error: undefined, setError: () => {} })

export const MetaMaskProvider = ({ children }: { children: ReactElement }) => {
  const [error, setError] = useState<Error>()
  const value = useMemo(() => ({ error, setError }), [error])
  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
}

export const useMetaMask = () => {
  const { error: connectError, setError } = useContext(ErrorContext)
  const isActive = metaMaskHooks.useIsActive()
  const activating = metaMaskHooks.useIsActivating()
  const connectedChainId = metaMaskHooks.useChainId()
  const connectedAccount = metaMaskHooks.useAccount()

  const unsupportedChain =
    isActive &&
    connectedChainId !== undefined &&
    !SUPPORTED_CHAIN_IDS.includes(connectedChainId)
  const active = isActive && !unsupportedChain
  const chainId = active ? connectedChainId : undefined
  const account = active ? connectedAccount : undefined

  // Recreated on every chain or account change, as ethers v5 providers can't
  // follow a network switch
  const library = useMemo(() => {
    if (!active || !metaMask.provider) return undefined
    const provider = new ethers.providers.Web3Provider(
      metaMask.provider as ethers.providers.ExternalProvider
    )
    provider.pollingInterval = 12000
    return provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, chainId, account])

  const connect = useCallback(async () => {
    setError(undefined)
    try {
      await metaMask.activate()
    } catch (error) {
      setError(error as Error)
    }
  }, [setError])

  const disconnect = useCallback(() => {
    setError(undefined)
    metaMask.resetState()
  }, [setError])

  return {
    active,
    activating,
    error:
      connectError ??
      (unsupportedChain && connectedChainId !== undefined
        ? new UnsupportedChainIdError(connectedChainId)
        : undefined),
    chainId,
    account,
    library,
    connect,
    disconnect,
  }
}
